import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  useTheme,
  Paper,
} from '@mui/material';
import { Resource } from '../../types/resource';
import { WorkingHour } from '../../types/workingHours';
import { Pencil, Trash2, Clock, Mail, Phone, Calendar } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import ResourceServices from '../../components/resources/ResourceServices';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';

interface ResourceDetailProps {
  resource: Resource;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const DetailField = ({
  label,
  value,
  chip = false,
}: {
  label: string;
  value: string | number | null | undefined;
  chip?: boolean;
}) => {
  const theme = useTheme();

  if (chip && value) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: `${theme.palette.primary.main}15`,
            color: theme.palette.primary.main,
            fontSize: '0.865rem',
            height: 24,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
        {value || 'Not provided'}
      </Typography>
    </Box>
  );
};

const ContactField = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | number | null | undefined;
  icon: React.ElementType;
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant="subtitle2"
        color="primary"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Icon size={16} />
      </Typography>
      <Typography variant="body2" color="text.primary">
        {value || 'Not provided'}
      </Typography>
    </Box>
  );
};

const WorkingHoursSection = ({ workingHours }: { workingHours: WorkingHour[] }) => {
  const theme = useTheme();
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const formatTime = (time: string) => {
    if (time.includes('am') || time.includes('pm')) {
      return time;
    }
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        color="primary"
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Clock size={16} />
        Working Hours
      </Typography>
      <Grid container spacing={1}>
        {days.map(day => {
          const schedules = workingHours.filter(wh => wh.day.toLowerCase() === day);
          const isWorkingDay = schedules.length > 0;

          return (
            <Grid item xs={12} key={day}>
              <Paper
                variant="outlined"
                sx={{
                  px: 0,
                  py: 1,
                  // backgroundColor: isWorkingDay ? theme.palette.grey[50] : undefined,
                  border: 0,
                  borderBottom: 1,
                  borderRadius: 0,
                  borderColor: theme.palette.divider,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isWorkingDay ? theme.palette.primary.main : theme.palette.text.secondary,
                    textTransform: 'capitalize',
                    width: 100,
                  }}
                >
                  {day}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  {isWorkingDay ? (
                    schedules.map((schedule, index) => (
                      <Typography
                        variant="body2"
                        color="text.primary"
                        key={`${day}-${schedule.startTime}-${schedule.endTime}-${index}`}
                      >
                        {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Closed
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const ResourceDetail: React.FC<ResourceDetailProps> = ({ resource, onClose, onEdit, onDelete }) => {
  const { role } = useAuth();
  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Staff Member Details</Typography>
          {role !== AUTH_CONSTANTS.ROLES.USER ? (
            <Box>
              <Button startIcon={<Pencil size={16} />} onClick={onEdit} sx={{ mr: 1 }}>
                Edit
              </Button>
              <Button startIcon={<Trash2 size={16} />} color="error" onClick={onDelete}>
                Delete
              </Button>
            </Box>
          ) : null}
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {resource.name}
                </Typography>
                {resource.description && (
                  <Typography variant="body2" color="text.secondary">
                    {resource.description}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}
              >
                <ContactField label="email" value={resource.email} icon={Mail} />
                <ContactField label="phone" value={resource.phone} icon={Phone} />
              </Box>
            </Box>
            <Divider />
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, md: 4, lg: 8 }} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <DetailField label="Timezone" value={resource.timezone} chip />
                  <DetailField label="Booking Per Session" value={resource.bookingsPerSession} />
                  {resource.defaultDuration && (
                    <DetailField
                      label="Default Duration"
                      value={`${resource.defaultDuration} minutes`}
                    />
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Calendar size={16} />
                    Services
                  </Typography>
                  <ResourceServices services={resource.serviceResourceMapping || []} />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  <WorkingHoursSection workingHours={resource.workingHours || []} />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* System Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              System Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailField label="Created At" value={formatDate(resource.createdAt)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Last Updated" value={formatDate(resource.updatedAt)} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </>
  );
};

export default ResourceDetail;
